import transformValue from "./transformers/transform-value";
import _ from "lodash";
import useStore from "./store";
import { HasManyRelation } from "./decorators";

class BaseModel {
  id;
  __type;

  static assignAttributes(obj, attrs) {
    const attributeTypes = obj.constructor.attributes || {};
    for (const [key, type] of Object.entries(attributeTypes)) {
      let snakeCaseKey = _.snakeCase(key);
      if (attrs !== null && attrs[snakeCaseKey] !== undefined) {
        let a = transformValue(attrs[snakeCaseKey], type);
        obj[key] = a;
      }
    }

    obj.id = attrs.id ?? attrs.pk;
  }

  static assignRelationships(obj, attrs, opts = {}) {
    const relationships = obj.constructor.relationships || [];

    for (const { modelName, propertyName } of relationships) {
      const attrKey = _.snakeCase(propertyName);
      const value = attrs[attrKey];
      if (value === undefined) continue;

      const hasMany = modelName instanceof HasManyRelation;
      const newModelName =
        hasMany ? modelName.className : modelName;

      const ModelClass = opts.modelRegistry[newModelName];
      if (!ModelClass) continue;

      if (hasMany && Array.isArray(value)) {
        obj[propertyName] = obj[propertyName] || [];
        for (const item of value) {
          const relatedInstance = new ModelClass();
          if(item){
            this.assignAttributes(relatedInstance, item);
            this.assignRelationships(relatedInstance, item, opts);
          }
          obj[propertyName].push(relatedInstance);
        }
      } else {
        const relatedInstance = new ModelClass();
        if(value){
          this.assignAttributes(relatedInstance, value);
          this.assignRelationships(relatedInstance, value, opts);
        }
        obj[propertyName] = relatedInstance;
      }
    }
  }

  static fromJson(json, type, opts = {}) {
    let a = new this();
    a.__type = type;
    this.assignAttributes(a, json, opts);
    this.assignRelationships(a, json, opts);
    return a;
  }

  serialize() {
    const attributeTypes = this.constructor.attributes || {};

    return JSON.stringify(
      Object.fromEntries(
        Object.entries(this)
          .filter(([key]) => attributeTypes.hasOwnProperty(key))
          .map(([key, value]) => [
            _.snakeCase(key),
            serializeValue(value, attributeTypes[key]),
          ])
      )
    );
  }

  async save(settings) {
    return await useStore.getState().__save(this, settings);
  }
}

// Function to handle serialization before sending to API
function serializeValue(value, type) {
  if (value == null) return value;

  switch (type) {
    case "datetime":
      return value instanceof Date ? value.toISOString() : value;
    case "number":
      return Number(value);
    case "boolean":
      return Boolean(value);
    case "string":
    default:
      return String(value);
  }
}

export default BaseModel;
